import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollFadeWrapper } from "@/components/ui/scroll-fade-wrapper";
import { ArrowLeft, Save, Loader2, KeyRound, User, Shield, Bell, CreditCard, CheckCheck, LogOut } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import BillingSettings from "@/components/settings/BillingSettings";
import ProfileSettings from "@/components/settings/ProfileSettings";
import Navbar from "@/components/Navbar";

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [notifSaving, setNotifSaving] = useState(false);

  const [notifications, setNotifications] = useState({
    message_notifications: true,
    appointment_reminders: true,
    assessment_updates: true,
    prescription_updates: true,
    marketing_emails: false,
  });

  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!user) return;
    const fetchNotifications = async () => {
      setLoading(true);
      const { data: notifData } = await supabase
        .from("notification_preferences")
        .select("message_notifications, appointment_reminders, assessment_updates, prescription_updates, marketing_emails")
        .eq("user_id", user.id)
        .single();

      if (notifData) {
        setNotifications({
          message_notifications: notifData.message_notifications,
          appointment_reminders: notifData.appointment_reminders,
          assessment_updates: notifData.assessment_updates,
          prescription_updates: notifData.prescription_updates,
          marketing_emails: notifData.marketing_emails,
        });
      }
      setLoading(false);
    };
    fetchNotifications();
  }, [user]);

  const handleChangePassword = async () => {
    if (passwords.newPassword.length < 6) {
      toast({ title: "Too short", description: "Password must be at least 6 characters.", variant: "destructive" });
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast({ title: "Mismatch", description: "Passwords do not match.", variant: "destructive" });
      return;
    }
    setPasswordSaving(true);
    const { error } = await supabase.auth.updateUser({ password: passwords.newPassword });
    setPasswordSaving(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Password updated", description: "Your password has been changed." });
      setPasswords({ newPassword: "", confirmPassword: "" });
    }
  };

  const handleSaveNotifications = async () => {
    if (!user) return;
    setNotifSaving(true);
    const { error } = await supabase
      .from("notification_preferences")
      .upsert({ user_id: user.id, ...notifications }, { onConflict: "user_id" });
    setNotifSaving(false);
    if (error) {
      toast({ title: "Error", description: "Failed to save notification preferences.", variant: "destructive" });
    } else {
      toast({ title: "Preferences saved", description: "Your notification settings have been updated." });
    }
  };

  const toggleNotification = (key: keyof typeof notifications) => {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [showDeselectConfirm, setShowDeselectConfirm] = useState(false);

  const allOn = Object.values(notifications).every(Boolean);
  const setAllNotifications = (value: boolean) => {
    setNotifications({
      message_notifications: value,
      appointment_reminders: value,
      assessment_updates: value,
      prescription_updates: value,
      marketing_emails: value,
    });
  };
  const toggleAll = () => {
    if (allOn) {
      setShowDeselectConfirm(true);
    } else {
      setAllNotifications(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 pt-24 pb-16 max-w-2xl">
        <button
          onClick={() => navigate("/dashboard")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <h1 className="font-display text-3xl text-foreground mb-1">Settings</h1>
        <p className="text-muted-foreground mb-8">Manage your profile and account preferences.</p>

        <Tabs defaultValue="profile" className="space-y-6">
          <ScrollFadeWrapper>
          <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 p-1 w-full justify-start bg-muted">
            <TabsTrigger value="profile" className="gap-1.5 shrink-0">
              <User size={14} /> Profile
            </TabsTrigger>
            <TabsTrigger value="billing" className="gap-1.5 shrink-0">
              <CreditCard size={14} /> Billing
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-1.5 shrink-0">
              <Bell size={14} /> Emails
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-1.5 shrink-0">
              <KeyRound size={14} /> Security
            </TabsTrigger>
            <TabsTrigger value="account" className="gap-1.5 shrink-0">
              <Shield size={14} /> Account
            </TabsTrigger>
          </TabsList>
          </ScrollFadeWrapper>

          {/* Profile Tab */}
          <TabsContent value="profile">
            {user && <ProfileSettings user={user} />}
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <BillingSettings />
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Email Notifications</CardTitle>
                <CardDescription>Choose which emails you'd like to receive from Elara Health.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                <div className="flex items-center justify-between pb-2">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    <CheckCheck size={14} />
                    {allOn ? "Deselect All" : "Select All"}
                  </button>
                </div>
                <Separator />
                {[
                  {
                    key: "message_notifications" as const,
                    title: "Message Notifications",
                    desc: "Get notified when you receive a new message from your care team.",
                  },
                  {
                    key: "appointment_reminders" as const,
                    title: "Appointment Reminders",
                    desc: "Receive email reminders before upcoming consultations.",
                  },
                  {
                    key: "assessment_updates" as const,
                    title: "Assessment Updates",
                    desc: "Get notified when your health assessment status changes.",
                  },
                  {
                    key: "prescription_updates" as const,
                    title: "Prescription Updates",
                    desc: "Notifications about prescription status and pharmacy fulfillment.",
                  },
                  {
                    key: "marketing_emails" as const,
                    title: "Wellness Tips & Promotions",
                    desc: "Occasional emails with health tips, new therapies, and exclusive offers.",
                  },
                ].map((item, i, arr) => (
                  <div key={item.key}>
                    <div className="flex items-center justify-between py-4">
                      <div className="space-y-0.5 pr-4">
                        <p className="text-sm font-medium text-foreground">{item.title}</p>
                        <p className="text-xs text-muted-foreground">{item.desc}</p>
                      </div>
                      <Switch
                        checked={notifications[item.key]}
                        onCheckedChange={() => toggleNotification(item.key)}
                      />
                    </div>
                    {i < arr.length - 1 && <Separator />}
                  </div>
                ))}

                <div className="flex justify-end pt-4">
                  <Button onClick={handleSaveNotifications} disabled={notifSaving} className="gap-2">
                    {notifSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save Preferences
                  </Button>
                </div>
              </CardContent>
            </Card>
            <AlertDialog open={showDeselectConfirm} onOpenChange={setShowDeselectConfirm}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Turn off all notifications?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will disable all email notifications including messages from your care team, appointment reminders, and health updates. You can re-enable them anytime.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => setAllNotifications(false)}>
                    Turn Off All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Change Password</CardTitle>
                <CardDescription>Update your account password.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={passwords.newPassword}
                    onChange={(e) => setPasswords((p) => ({ ...p, newPassword: e.target.value }))}
                    placeholder="At least 6 characters"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords((p) => ({ ...p, confirmPassword: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={handleChangePassword} disabled={passwordSaving} className="gap-2">
                    {passwordSaving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
                    Update Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card className="border-border bg-card">
              <CardHeader>
                <CardTitle className="text-lg">Account</CardTitle>
                <CardDescription>Manage your Elara Health account.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium text-foreground text-sm">Email</p>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium text-foreground text-sm">Member since</p>
                    <p className="text-sm text-muted-foreground">
                      {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Data & Privacy</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Your health data is encrypted and protected under HIPAA regulations. To request data export or account deletion, contact our support team.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/hipaa")}>
                    View HIPAA Notice
                  </Button>
                </div>

                <Separator />

                <div>
                  <p className="text-sm font-medium text-foreground mb-1">Sign Out</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Sign out of your Elara Health account on this device.
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    className="gap-2"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      navigate("/login");
                    }}
                  >
                    <LogOut size={14} />
                    Sign Out
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
